import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { PopoverInfoProduction } from './components/PopoverInfoProduction/PopoverInfoProduction';
import { QualityMeasures } from './components/QualityMeasures/QualityMeasures';
import { IconWithoutReason } from './components/IconWithoutReason';
import { TimeTicker } from './components/TimeTicker/TimeTicker';
import { TimeGridProps, TypeCell } from './TimeGrid.types';
import { AllieClockIcon, AllieRepeatIcon } from '../../../styling/icons';
import {
  TableContent,
  TableHeaderRate,
  TableHeaderText,
  TimeGridContainer,
} from './TimeGrid.styles';
import { AllieText } from '../../../components/Text';
import { useClickStopPopover } from './utils/hooks';
import { TextCell } from './components/TextCell';
import { getCellsMapped } from './utils/helpers';
import { TypesCell } from './utils/enums';
import { ProducedRate } from './components/ProducedRate';
import { ProgressEstimated } from './components/ProgressEstimated';
import { EditableProducedRate } from './components/EditableProducedRate/EditableProducedRate';
import { isSmartTV } from '../../../common/utils';

dayjs.extend(utc);
dayjs.extend(tz);

const namespace = 'allie-time-grid';

export const AllieTimeGrid: FC<TimeGridProps> = ({
  data = [],
  yAxisLabels = [],
  dataUnits,
  onClickOpenModalReason,
  labelsPopoverAddStop,
  startAtProductions,
  renderPopoverInfoProduction,
  timezone,
  showTimeTicker = true,
  isCurrentShift,
  onNewStop,
  qualityMeasures = [],
  onClickQualityMeasure,
  heightRow = 50,
  heightMinutesRow = 40,
  onClickAddTechnicalStop,
  showAddTechnicalStopMenu,
  isCompactView = false,
  renderAssignReasonForm,
  shouldScrollIntoView = true,
  redThreshold,
  greenThreshold,
  isProductionEditMode = false,
  onProductionValueChange,
  hoursEditables,
  onClickDeleteStop,
  massSelectMode = false,
  selectedStopIds,
  onToggleStopSelection,
  isCellSelectable,
}) => {
  dayjs.tz.setDefault(timezone);
  const [newStopIds, setNewStopIds] = useState<Array<number>>([]);
  const [divResize, setDivResize] = useState<JSX.Element>();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    handleClickStop,
    handleClickUpdateReason,
    removeDinamicNode,
    showBackdrop,
    setShowBackdrop,
  } = useClickStopPopover(
    setDivResize,
    onClickOpenModalReason,
    labelsPopoverAddStop,
    timezone,
    yAxisLabels,
    onClickAddTechnicalStop,
    renderAssignReasonForm,
    showAddTechnicalStopMenu,
    onClickDeleteStop
  );

  const yHours = yAxisLabels;
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => (i + 1).toString()), []);
  const minutesHeader = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ((i + 1) * 5).toString()),
    []
  );

  const removeBackdrop = () => {
    setShowBackdrop(false);
    removeDinamicNode();
  };

  const gridCells = getCellsMapped(yHours, minutes);

  const disponibilityData = data.reduce(
    (
      acc: Array<{
        startHour: number;
        startMinute: number;
        endHour: number;
        endMinute: number;
        id?: number;
        startAt: number;
        endAt: number;
        value: any;
        type: any;
        stopTypeName?: string;
        categoryName?: string;
      }>,
      item
    ) => {
      const startDate = dayjs.unix(item.startAt).tz(timezone);
      const endDate = dayjs.unix(item.endAt).tz(timezone);

      const startHour = startDate.hour();
      const endHour = endDate.hour();

      if (startHour > endHour) {
        acc.push({
          ...item,
          startHour: startHour,
          startMinute: startDate.minute() + 1,
          endHour: 24,
          endMinute: 0,
        });

        acc.push({
          ...item,
          startHour: 0,
          startMinute: 1,
          endHour: endHour,
          endMinute: endDate.minute(),
        });
      } else {
        acc.push({
          ...item,
          startHour: startHour,
          startMinute: startDate.minute() + 1,
          endHour: endHour,
          endMinute: endDate.minute(),
        });
      }

      return acc;
    },
    []
  );

  let lastHour = -1;
  let lastMinute = -1;

  disponibilityData.forEach((item) => {
    if ((lastHour < 12 && item.endHour >= 12) || (lastHour >= 12 && item.endHour < 12)) {
      lastHour = item.endHour;
    } else {
      lastHour = item.endHour > lastHour && item.endMinute > 0 ? item.endHour : lastHour;
    }

    for (let hour = item.startHour; hour <= item.endHour; hour++) {
      const startMinute = hour === item.startHour ? item.startMinute : 1;
      const endMinute = hour === item.endHour ? item.endMinute : 60;

      let count = item.value.groupable ? endMinute - startMinute + 1 : 1;
      const countTotalHour = endMinute - startMinute;

      let isFirts = true;
      for (let minute = startMinute; minute <= endMinute; minute++) {
        lastMinute = hour === lastHour ? minute : lastMinute;
        let cell = gridCells.get(hour)?.minutes.get(minute);

        if (cell) {
          if (cell.type) cell = {};
          if (isFirts || !item.value.groupable) {
            if (count > endMinute - minute) {
              count = endMinute - minute + 1;
            }
            gridCells
              .get(hour)!
              .minutes.set(minute, { ...cell, ...item, ...item.value, count, countTotalHour });
            isFirts = false;
          } else {
            gridCells.get(hour)!.minutes.delete(minute);
          }
        }
      }
    }
  });

  startAtProductions?.forEach((item) => {
    const startDate = dayjs(item.startAt).tz(timezone);
    let startHour = startDate.hour();
    let startMinute = startDate.minute() + 1;
    let cell = gridCells.get(startHour)?.minutes.get(startMinute);
    if (cell) {
      gridCells.get(startHour)!.minutes.set(startMinute, { ...cell, startProductionId: item.id });
    } else {
      startMinute++;
      if (startMinute <= 60) {
        cell = gridCells.get(startHour)?.minutes.get(startMinute);
        cell &&
          gridCells
            .get(startHour)!
            .minutes.set(startMinute, { ...cell, startProductionId: item.id });
      } else {
        startHour++;
        cell = gridCells.get(startHour)?.minutes.get(1);
        cell && gridCells.get(startHour)!.minutes.set(1, { ...cell, startProductionId: item.id });
      }
    }
  });

  let prevId = -100;

  const isCellClickable = (typeCell: string) => {
    return [TypesCell.PLANNED_STOP, TypesCell.UNPLANNED_STOP, TypesCell.TECHNICAL_STOP].includes(
      typeCell as TypesCell
    );
  };

  const isCellStop = (typeCell: string) => {
    return [
      TypesCell.PLANNED_STOP,
      TypesCell.UNPLANNED_STOP,
      TypesCell.TECHNICAL_STOP,
      TypesCell.REDUCTION,
    ].includes(typeCell as TypesCell);
  };

  const showPointer = (type: TypeCell | undefined) => {
    return isCellClickable(type as string) ? 'pointer' : 'default';
  };

  // BEV2-4 bulk assignment: a cell is selectable in mass mode only if it's a kind the grid
  // would otherwise let the operator click, and (when given) the caller's own eligibility
  // check — e.g. "only stops with no reason yet" — agrees.
  const isSelectableInMassMode = (type: TypeCell | undefined, cell: any) =>
    massSelectMode && isCellClickable((type as string) || '') && (!isCellSelectable || isCellSelectable(cell));

  const thereAreNewStop = useCallback(() => {
    if (isCurrentShift) {
      const lastCell = gridCells.get(lastHour)?.minutes.get(lastMinute);
      if (
        lastCell &&
        lastCell.type === TypesCell.UNPLANNED_STOP &&
        lastCell.startHour === lastHour &&
        lastCell.startMinute === lastMinute &&
        !newStopIds.includes(lastCell.id || 0)
      ) {
        setNewStopIds((prev) => [...prev, lastCell.id || 0]);
        onNewStop();
      }
    }
  }, [lastHour, lastMinute, gridCells, onNewStop, isCurrentShift]);

  thereAreNewStop();

  useEffect(() => {
    if (containerRef.current && shouldScrollIntoView) {
      const lastHourDiv = containerRef.current.querySelector(`div[data-hour="${lastHour}"]`);
      if (lastHourDiv) {
        lastHourDiv.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
      } else {
        containerRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [containerRef.current?.offsetHeight, lastHour, isCompactView]);

  return (
    <>
      <TimeGridContainer ref={containerRef} className={namespace}>
        <div
          className={classnames('row', {
            'wihout-produced': !dataUnits.units.length,
            'row-tv': isSmartTV,
            'production-edit-mode': isProductionEditMode && dataUnits.units.length > 0,
          })}
          style={{ height: heightMinutesRow }}
        >
          <AllieText color="neutral-80" className="label center" key="hour">
            <div className="timegrid-icon">
              <AllieClockIcon color="neutral-60" allieColor="neutral-60" />
            </div>
          </AllieText>
          {minutesHeader.map((minute) => (
            <TableHeaderText
              className="label minute-header"
              type="body2"
              color="neutral-60"
              key={minute}
            >{`${minute} m`}</TableHeaderText>
          ))}
          {dataUnits.units.length > 0 && (
            <>
              {isProductionEditMode ? (
                <TableHeaderRate
                  type="body3"
                  weight={600}
                  color="neutral-80"
                  className="label center"
                  style={{ gridColumn: 'span 2' }}
                >
                  {`${dataUnits.label} (${dataUnits.unitLabel})`}
                </TableHeaderRate>
              ) : (
                <>
                  <div>
                    <TableHeaderRate
                      type="body3"
                      weight={600}
                      color="neutral-80"
                      className="label center"
                    >
                      {dataUnits.label}
                    </TableHeaderRate>
                    <TableHeaderRate
                      type="body3"
                      weight={600}
                      color="neutral-80"
                      className="label center"
                    >
                      {dataUnits.unitLabel}
                    </TableHeaderRate>
                  </div>

                  <TableHeaderRate
                    type="body3"
                    weight={600}
                    color="neutral-80"
                    className="label center"
                  >
                    {dataUnits.rateLabel}
                  </TableHeaderRate>
                </>
              )}
            </>
          )}
        </div>
        <TableContent $compactView={isCompactView}>
          {Array.from(gridCells.entries()).map(
            ([hourNumber, { minutes: minuteMap, index: indexHour }]) => {
              const hour = hourNumber;
              let indexMinute = 0;

              return (
                <div
                  key={hour}
                  className={classnames('cells', 'row', {
                    'wihout-produced': !dataUnits.units.length,
                    'row-tv': isSmartTV,
                    'production-edit-mode': isProductionEditMode && dataUnits.units.length > 0,
                  })}
                  style={{ height: heightRow }}
                >
                  <TableHeaderText color="neutral-80" className="label center" key={`hour-${hour}`}>
                    {hour.toString().padStart(2, '0')}
                  </TableHeaderText>
                  {Array.from(minuteMap.entries()).map(([minuteLabel, cellData], index) => {
                      const {
                        color,
                        reason,
                        stopReasonId,
                        count,
                        startAt,
                        endAt,
                        hex,
                        id,
                        labelColor,
                        startProductionId,
                        type,
                        countTotalHour,
                        groupable,
                        machineId,
                        componentId,
                        stopTypeName,
                        machineName,
                        categoryName,
                        note,
                        failureModeId,
                        failureModeName,
                      } = cellData;
                      indexMinute += count || 1;
                      const isNewId = prevId !== id;
                      prevId = id!;
                      const isSelectable = isSelectableInMassMode(type, cellData);
                      const isSelected = massSelectMode && !!id && !!selectedStopIds?.has(id);
                      return (
                        <div
                          key={`${hour}-${minuteLabel}`}
                          data-min-init={startAt}
                          data-min-end={endAt}
                          data-id={id || 0}
                          data-minute={minuteLabel}
                          data-count={count}
                          data-index={indexMinute}
                          data-type={type}
                          data-hex={hex}
                          data-color={color}
                          data-hour={hour}
                          data-first-minute={index === 0}
                          className={classnames('cell', 'data', {
                            'border-white': (indexMinute - count!) % 5 === 0,
                            [`allie-bg-${color}`]: !hex,
                            'is-clickable': isCellClickable(type || '') && !massSelectMode,
                            'border-init-stop':
                              isCellStop(type || '') && !((indexMinute - count!) % 5 === 0),
                            'border-init-warning':
                              type === TypesCell.REDUCTION && !((indexMinute - count!) % 5 === 0),
                            'mass-select-selectable': isSelectable,
                            'mass-select-dimmed': massSelectMode && isCellClickable(type || '') && !isSelectable,
                            'mass-select-selected': isSelected,
                          })}
                          style={{
                            gridColumn: `span ${count}`,
                            ...(hex && { backgroundColor: hex }),
                            cursor: massSelectMode ? (isSelectable ? 'pointer' : 'default') : showPointer(type),
                          }}
                          {...(isSelectable && {
                            role: 'button',
                            tabIndex: 0,
                            'aria-pressed': isSelected,
                            onClick: () => onToggleStopSelection?.(id!),
                            onKeyDown: (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onToggleStopSelection?.(id!);
                              }
                            },
                          })}
                          {...(!massSelectMode &&
                            isCellClickable(type || '') &&
                            !reason && {
                              onClick: (e) =>
                                handleClickStop(e, {
                                  stopType: (type && type.split('_')[0]) || '',
                                }),
                            })}
                          {...(!massSelectMode &&
                            isCellClickable(type || '') &&
                            reason &&
                            stopReasonId && {
                              onClick: (e) =>
                                handleClickUpdateReason(e, {
                                  reason,
                                  stopReasonId,
                                  stopType: (type && type.split('_')[0]) || '',
                                  machineId: machineId,
                                  machineName,
                                  componentId: componentId,
                                  stopTypeName,
                                  categoryName,
                                  note,
                                  failureModeId,
                                  failureModeName,
                                }),
                            })}
                        >
                          {/* isNewId: a non-groupable stop spans several 1-minute cells sharing
                              the same id (see the merge logic above and TextCell/
                              IconWithoutReason's own isNewId guard) — without it this badge
                              would repeat once per minute across the whole span. */}
                          {isSelected && isNewId && <span className="mass-select-badge">✓</span>}
                          {startProductionId && (
                            <>
                              <AllieRepeatIcon
                                id={`icon-start-production-${startProductionId}`}
                                allieColor="primary-60"
                                className="icon-start-production"
                              />
                              <PopoverInfoProduction
                                triggerId={`icon-start-production-${startProductionId}`}
                                render={() => renderPopoverInfoProduction(startProductionId)}
                              />
                            </>
                          )}
                          <TextCell
                            reason={categoryName && reason ? `${categoryName} - ${reason}` : reason}
                            isNewId={isNewId}
                            labelColor={labelColor}
                            countTotal={countTotalHour}
                            isGroupable={groupable}
                          />
                          <IconWithoutReason
                            reason={reason}
                            type={type}
                            isNewId={isNewId}
                            count={count}
                          />
                          {hour === lastHour && indexMinute === lastMinute && showTimeTicker && (
                            <TimeTicker
                              height={heightRow * yHours.length}
                              top={-heightRow * indexHour - indexHour}
                            />
                          )}
                        </div>
                      );
                    }
                  )}
                  {dataUnits.units.length > 0 && (
                    <>
                      {isProductionEditMode ? (
                        <EditableProducedRate
                          produced={dataUnits.units[indexHour]?.producedNumber ?? 0}
                          hour={hour}
                          onValueChange={onProductionValueChange}
                          disabled={!hoursEditables?.includes(hour)}
                        />
                      ) : (
                        <>
                          <ProducedRate
                            produced={dataUnits.units[indexHour]?.produced ?? ''}
                            rate={dataUnits.units[indexHour]?.rate}
                            showArrow={dataUnits.showArrow}
                          />
                          <ProgressEstimated
                            progress={dataUnits.units[indexHour]?.progress}
                            estimated={dataUnits.units[indexHour]?.estimated}
                            redThreshold={redThreshold}
                            greenThreshold={greenThreshold}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            }
          )}
        </TableContent>
        {showBackdrop && (
          <div
            onClick={() => removeBackdrop()}
            className={`no-grid ${showBackdrop ? 'show-backdrop' : ''}`}
          ></div>
        )}
        <QualityMeasures
          containerRef={containerRef}
          hours={yAxisLabels}
          qualityMeasures={qualityMeasures}
          timezone={timezone}
          onClick={onClickQualityMeasure}
          startAtProductions={startAtProductions}
          heightRow={heightRow}
          heightMinutes={heightMinutesRow}
        />
        <div>{divResize}</div>
      </TimeGridContainer>
    </>
  );
};
